/**
 * Shared Google Geocoding REST helper (Node scripts + browser REST fallback).
 * Single source for address → { lat, lng } via Geocoding API.
 */

/**
 * @param {string} address
 * @param {string} apiKey
 * @returns {Promise<{ ok: true, lat: number, lng: number } | { ok: false, reason: string }>}
 */
export async function geocodeLocationRest(address, apiKey) {
  const trimmed = String(address || '').trim();
  if (!trimmed) return { ok: false, reason: 'empty_address' };
  if (!apiKey) return { ok: false, reason: 'missing_api_key' };

  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(trimmed)}&key=${apiKey}`;
    const res = await fetch(url);
    if (!res.ok) return { ok: false, reason: `http_${res.status}` };
    const data = await res.json();
    if (data.status !== 'OK' || !data.results?.[0]?.geometry?.location) {
      return { ok: false, reason: data.status || 'no_results' };
    }
    const { lat, lng } = data.results[0].geometry.location;
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return { ok: false, reason: 'invalid_coords' };
    }
    return { ok: true, lat, lng };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : 'network_error' };
  }
}

/** Build a geocodable US address string from parts. */
export function formatUsAddress(parts) {
  const street = String(parts.street || '').trim();
  const city = String(parts.city || '').trim();
  const state = String(parts.state || '').trim();
  const zip = String(parts.zip || '').trim();
  const segments = [street, city, state, zip].filter(Boolean);
  if (segments.length === 0) return '';
  const base = segments.join(', ');
  return base.toLowerCase().includes('usa') ? base : `${base}, USA`;
}
